// src/components/Header/Header.js
import * as React from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import {
  Box,
  Typography,
  Button,
  IconButton,
  Sheet,
  Stack,
  Avatar,
  Chip,
  Tooltip,
  Dropdown,
  Menu,
  MenuButton,
  MenuItem,
  ListDivider,
  Snackbar,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemDecorator
} from '@mui/joy'
import LogoutIcon from '@mui/icons-material/Logout'
import {
  AutoStoriesRounded,
  SchoolRounded,
  MenuBookRounded,
  BugReportRounded,
  LogoutRounded,
  MenuRounded,
  CloseRounded,
  PersonRounded,
  SettingsRounded,
  TimelineRounded,
  TimerRounded,
  PlayArrow,
  Pause,
  Brightness4,
  Brightness7,
  LoginRounded,
  PersonAddRounded,
  AutoAwesomeRounded,
  PublicRounded,
  SellRounded,
  MailOutlineRounded,
  SpaceDashboardRounded
} from '@mui/icons-material'
import { usePomodoro } from '@nowry/core/context/PomodoroContext'
import { formatClock } from '@nowry/core/utils/formatClock'
import { usePet } from '@nowry/core/context/AgentContext'
import { Z_NAV } from '@nowry/core/constants/zIndex'
import { useColorScheme } from '@mui/joy/styles'
import { BrandLockup } from '../Common/Brand/BrandMark'
import { useTranslation } from 'react-i18next'
import BugReportModal from '../Bugs/BugReportModal'
import { bugsService } from '@nowry/core/api/services/bugs.service'
import { useAuth } from '@nowry/core/context/AuthContext'
import { useBeta } from '@nowry/core/context/BetaContext'

const HeaderUtils = ({ variant = 'header' }) => {
  const { mode, setMode } = useColorScheme()
  const { i18n, t } = useTranslation()
  const languages = [
    { code: 'en', label: 'EN' },
    { code: 'es', label: 'ES' },
    { code: 'fr', label: 'FR' },
    { code: 'de', label: 'DE' },
    { code: 'ja', label: 'JA' }
  ]

  const isDrawer = variant === 'drawer'
  const focusSx = {
    '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.outlinedBorder', outlineOffset: '2px' }
  }
  const colorSx = isDrawer
    ? {
        color: 'text.secondary',
        '&:hover': { bgcolor: 'background.level2', color: 'text.primary' },
        ...focusSx
      }
    : {
        // The header variant renders for guests only, on the page ground (ADR-035 §3).
        color: 'text.secondary',
        '&:hover': { bgcolor: 'background.level1', color: 'text.primary' },
        ...focusSx
      }

  return (
    <Stack direction='row' spacing={0.5} alignItems='center'>
      <Dropdown>
        <Tooltip title={t('common.language')} placement={isDrawer ? 'top' : 'bottom'}>
          <MenuButton variant='plain' size='sm' aria-label={t('common.language')} sx={{ minWidth: 'unset', px: 1, ...colorSx }}>
            {(languages.find((l) => l.code === i18n.language) || languages[0]).label}
          </MenuButton>
        </Tooltip>
        <Menu size='sm' sx={{ zIndex: 99999, minWidth: 100 }}>
          {languages.map((lang) => (
            <MenuItem key={lang.code} onClick={() => i18n.changeLanguage(lang.code)} selected={i18n.language === lang.code}>
              {lang.label}
            </MenuItem>
          ))}
        </Menu>
      </Dropdown>
      <Tooltip title={t('common.toggleTheme')} placement={isDrawer ? 'top' : 'bottom'}>
        <IconButton
          variant='plain'
          size='sm'
          onClick={() => setMode(mode === 'dark' ? 'light' : 'dark')}
          sx={colorSx}
          aria-label={t('common.toggleTheme')}
        >
          {mode === 'dark' ? <Brightness7 fontSize='small' /> : <Brightness4 fontSize='small' />}
        </IconButton>
      </Tooltip>
    </Stack>
  )
}

const Header = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout: contextLogout } = useAuth()
  const displayName = user?.username || user?.email?.split('@')[0] || ''
  const resolvedAvatarUrl = user?.avatar_url || user?.photo_url || user?.avatar || undefined
  const { t } = useTranslation()
  const { config: beta } = useBeta()
  const { petName, isInStudySession } = usePet()
  const { mode, setMode } = useColorScheme()

  // Bug report state
  const [bugReportOpen, setBugReportOpen] = React.useState(false)
  const [snackbar, setSnackbar] = React.useState({ open: false, message: '', color: 'success' })

  // Mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false)

  // Swipe gesture refs for mobile menu
  const touchStart = React.useRef(null)
  const touchEnd = React.useRef(null)
  const minSwipeDistance = 75 // Minimum swipe distance in px

  // Touch handlers for swipe gestures
  const handleTouchStart = React.useCallback((e) => {
    touchEnd.current = null
    touchStart.current = e.targetTouches[0].clientX
  }, [])

  const handleTouchMove = React.useCallback((e) => {
    touchEnd.current = e.targetTouches[0].clientX
  }, [])

  const handleTouchEnd = React.useCallback(() => {
    if (!touchStart.current || !touchEnd.current) return
    const distance = touchStart.current - touchEnd.current
    const isLeftSwipe = distance > minSwipeDistance
    const isRightSwipe = distance < -minSwipeDistance

    if (isLeftSwipe) {
      setMobileMenuOpen(true)
    }

    if (isRightSwipe && mobileMenuOpen) {
      setMobileMenuOpen(false)
    }
  }, [mobileMenuOpen])

  // Attach swipe listeners to body on mobile.
  // Disabled during active study sessions — StudySession.js owns all swipe
  // gestures on that screen (card advance / flip / voice settings). Without
  // this guard, a left-swipe on a card would simultaneously advance the card
  // AND open this drawer, because body listeners fire for every touch event
  // on the page regardless of the target element.
  React.useEffect(() => {
    const isMobile = window.innerWidth < 900 // md breakpoint
    if (!isMobile) return
    if (isInStudySession) return

    document.body.addEventListener('touchstart', handleTouchStart)
    document.body.addEventListener('touchmove', handleTouchMove)
    document.body.addEventListener('touchend', handleTouchEnd)

    return () => {
      document.body.removeEventListener('touchstart', handleTouchStart)
      document.body.removeEventListener('touchmove', handleTouchMove)
      document.body.removeEventListener('touchend', handleTouchEnd)
    }
  }, [mobileMenuOpen, handleTouchEnd, handleTouchMove, handleTouchStart, isInStudySession])

  // Pomodoro
  const { timeLeft, isActive: isTimerActive, isEnded: isTimerEnded, showWidget, setShowWidget, settings } = usePomodoro()
  // The menu's readout: the clock while running, the verdict while a session's
  // end waits for an answer (ADR-036).
  const timerReadout = isTimerEnded ? t('pomodoro.timesUp') : formatClock(timeLeft)
  const showTimerReadout = isTimerActive || isTimerEnded

  const logout = async () => {
    await contextLogout()
    navigate('/')
  }

  const handleBugSubmit = async (bugData) => {
    const response = await bugsService.submitBug(bugData)
    setSnackbar({
      open: true,
      message: response.message || 'Bug report submitted successfully!',
      color: 'success'
    })
    setBugReportOpen(false)
  }

  // A board's own page (/boards/:id) still reads as Boards in the bar (BOARD-003).
  const isActive = (path) => location.pathname === path || (path === '/boards' && location.pathname.startsWith('/boards/'))

  // One ground for guests and members (ADR-035, amended 2026-09-26): a nav link
  // reads on the page ground in both states.
  const navLinkSx = (path) => ({
    fontWeight: isActive(path) ? 'lg' : 'md',
    color: isActive(path) ? 'text.primary' : 'text.secondary',
    bgcolor: isActive(path) ? 'background.level1' : 'transparent',
    borderRadius: 'md',
    px: 1.5,
    '&:hover': { bgcolor: 'background.level1', color: 'text.primary' }
  })

  /** The guest header's nav: the library, pricing on the landing, contact. */
  const guestNav = [
    { name: t('header.library'), to: '/browse', path: '/browse', icon: <PublicRounded /> },
    { name: t('header.pricing'), to: { pathname: '/', hash: '#pricing' }, path: '/#pricing', icon: <SellRounded /> },
    { name: t('header.contact'), to: '/contact', path: '/contact', icon: <MailOutlineRounded /> }
  ]

  return (
    <>
      <Sheet
        component='header'
        sx={(theme) => ({
          // Pinned in both layouts: the fixed shell keeps it at the top by
          // construction, and on the document-flow guest pages it used to
          // scroll away with the page (a deep link to /#pricing landed with no
          // header at all). The sections it links to carry a scroll margin.
          position: 'sticky',
          top: 0,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: { xs: 2, md: 4 },
          py: 1.5,
          zIndex: Z_NAV,
          flexShrink: 0,
          // The page ground with a hairline under it, signed in or not (ADR-035 §3,
          // amended 2026-09-26). The header used to be the accent solid for
          // members and changed ground at sign-in; the accent is spent on the
          // Study key now, not on a bar.
          backgroundColor: theme.palette.background.body,
          color: theme.palette.text.primary
        })}
      >
        {/* Logo */}
        <Box
          component={Link}
          to='/'
          sx={{
            display: 'flex',
            alignItems: 'center',
            textDecoration: 'none',
            color: 'inherit',
            transition: 'opacity 0.2s',
            '&:hover': { opacity: 0.8 }
          }}
        >
          <BrandLockup markSize={30} />
          {beta.active && (
            <Chip size='sm' variant='soft' color='primary' sx={{ ml: 1 }} aria-label={t('beta.mark')}>
              {t('beta.mark')}
            </Chip>
          )}
        </Box>

        {/* Spacer */}
        <Box sx={{ flex: 1 }} />

        {/* Desktop Navigation */}
        <Stack direction='row' spacing={1} alignItems='center' sx={{ display: { xs: 'none', md: 'flex' } }}>
          {user ? (
            <>
              {/* Browse - logged nav, with icon */}
              <Button component={Link} to='/browse' variant='plain' size='sm' startDecorator={<SchoolRounded />} sx={navLinkSx('/browse')}>
                {t('public.browse')}
              </Button>
              {[
                { name: t('header.study'), path: '/study', icon: <AutoStoriesRounded /> },
                { name: t('header.books'), path: '/books', icon: <MenuBookRounded /> },
                { name: t('header.boards'), path: '/boards', icon: <SpaceDashboardRounded /> },
                { name: t('annualPlanning.title'), path: '/annual-planning', icon: <TimelineRounded /> }
              ].map((item) => (
                <Button
                  key={item.name}
                  component={Link}
                  to={item.path}
                  variant='plain'
                  size='sm'
                  startDecorator={item.icon}
                  sx={navLinkSx(item.path)}
                >
                  {item.name}
                </Button>
              ))}
            </>
          ) : (
            <>
              {guestNav.map((item) => (
                <Button key={item.path} variant='plain' color='neutral' component={Link} to={item.to} size='sm' sx={navLinkSx(item.path)}>
                  {item.name}
                </Button>
              ))}
            </>
          )}
        </Stack>

        {/* Right Section */}
        <Stack direction='row' spacing={1.5} alignItems='center'>
          {/* Mobile Menu Button */}
          <IconButton
            variant='plain'
            size='sm'
            onClick={() => setMobileMenuOpen(true)}
            sx={{
              display: { xs: 'flex', md: 'none' },
              color: 'text.primary',
              '&:hover': { bgcolor: 'background.level1' }
            }}
            aria-label={t('header.drawer.menu')}
          >
            <MenuRounded />
          </IconButton>

          {user ? (
            <>
              {/* Theme toggle — desktop only, authenticated users */}
              <Tooltip title={t('common.toggleTheme')} placement='bottom'>
                <IconButton
                  variant='plain'
                  size='sm'
                  onClick={() => setMode(mode === 'dark' ? 'light' : 'dark')}
                  aria-label={t('common.toggleTheme')}
                  sx={{
                    display: { xs: 'none', md: 'flex' },
                    color: 'text.secondary',
                    '&:hover': { bgcolor: 'background.level1', color: 'text.primary' },
                    '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.outlinedBorder', outlineOffset: '2px' }
                  }}
                >
                  {mode === 'dark' ? <Brightness7 fontSize='small' /> : <Brightness4 fontSize='small' />}
                </IconButton>
              </Tooltip>

              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <Dropdown>
                  <MenuButton
                    slots={{ root: 'div' }}
                    slotProps={{ root: { style: { cursor: 'pointer' } } }}
                    sx={{
                      p: 0,
                      minHeight: 'unset',
                      border: 'none',
                      background: 'none',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                      <Avatar
                        src={resolvedAvatarUrl}
                        size='sm'
                        sx={{
                          width: 32,
                          height: 32,
                          border: '2px solid',
                          borderColor: 'neutral.outlinedBorder',
                          borderRadius: '50%',
                          cursor: 'pointer'
                        }}
                        aria-label={t('header.profileMenu.open')}
                      />
                      {isTimerActive && (
                        <Box
                          sx={{
                            position: 'absolute',
                            inset: -3,
                            borderRadius: '50%',
                            border: '2px solid',
                            borderColor: 'primary.outlinedBorder',
                            animation: 'nowry-pulse 2s ease-in-out infinite',
                            '@keyframes nowry-pulse': {
                              '0%, 100%': { opacity: 1 },
                              '50%': { opacity: 0.35 }
                            },
                            pointerEvents: 'none'
                          }}
                        />
                      )}
                    </Box>
                  </MenuButton>

                  <Menu
                    placement='bottom-end'
                    size='sm'
                    sx={{
                      zIndex: 99999,
                      minWidth: 240,
                      maxWidth: 280,
                      p: 0.5,
                      '--ListItem-radius': 'var(--joy-radius-sm)'
                    }}
                  >
                    {/* Identity Panel — clickable, navigates to /profile */}
                    <MenuItem
                      component={Link}
                      to='/profile'
                      aria-label={t('header.profileMenu.viewProfile')}
                      sx={{
                        px: 2,
                        pt: 1.5,
                        pb: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        borderRadius: 'var(--joy-radius-sm)',
                        '&:hover': {
                          bgcolor: 'background.level1'
                        }
                      }}
                    >
                      <Avatar src={resolvedAvatarUrl} size='md' sx={{ width: 36, height: 36, flexShrink: 0 }} />
                      <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                        <Typography level='title-sm' sx={{ color: 'text.primary' }}>
                          {displayName}
                        </Typography>
                        <Typography level='body-xs' noWrap sx={{ color: 'text.tertiary', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {user?.email}
                        </Typography>
                        {user?.subscription?.tier && user.subscription.tier !== 'free' && (
                          <Box sx={{ mt: 0.25 }}>
                            <Chip size='sm' variant='soft' color='primary'>
                              {t(`header.profileMenu.tier.${user.subscription.tier}`)}
                            </Chip>
                          </Box>
                        )}
                      </Stack>
                    </MenuItem>

                    <ListDivider sx={{ my: 0.5 }} />

                    {/* Group 1 — Account */}
                    <MenuItem component={Link} to='/settings' sx={{ gap: 1.5 }}>
                      <ListItemDecorator sx={{ minWidth: 'unset' }}>
                        <SettingsRounded sx={{ fontSize: 18 }} />
                      </ListItemDecorator>
                      <Typography level='body-sm'>{t('common.settings')}</Typography>
                    </MenuItem>
                    <MenuItem component={Link} to='/settings/agent' sx={{ gap: 1.5 }}>
                      <ListItemDecorator sx={{ minWidth: 'unset' }}>
                        <AutoAwesomeRounded sx={{ fontSize: 18 }} />
                      </ListItemDecorator>
                      <Typography level='body-sm'>{petName || t('agent.defaultName')}</Typography>
                    </MenuItem>

                    {/* Group 2 — Pomodoro Tool (conditional) */}
                    {settings.enabled && (
                      <>
                        <ListDivider sx={{ my: 0.5 }} />
                        <MenuItem onClick={() => setShowWidget(!showWidget)} sx={{ gap: 1.5 }}>
                          <ListItemDecorator sx={{ minWidth: 'unset', position: 'relative' }}>
                            <TimerRounded sx={{ fontSize: 18 }} />
                            {isTimerActive && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: -2,
                                  right: -2,
                                  width: 6,
                                  height: 6,
                                  borderRadius: '50%',
                                  bgcolor: 'success.solidBg'
                                }}
                              />
                            )}
                          </ListItemDecorator>
                          <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ flex: 1 }}>
                            <Typography level='body-sm'>{t('common.pomodoro')}</Typography>
                            {showTimerReadout && (
                              <Typography level='body-xs' sx={{ color: 'text.tertiary', fontVariantNumeric: 'tabular-nums' }}>
                                {timerReadout}
                              </Typography>
                            )}
                          </Stack>
                        </MenuItem>
                      </>
                    )}

                    <ListDivider sx={{ my: 0.5 }} />

                    {/* Group 3 — Danger */}
                    <MenuItem onClick={logout} color='danger' sx={{ gap: 1.5 }}>
                      <ListItemDecorator sx={{ minWidth: 'unset' }}>
                        <LogoutRounded sx={{ fontSize: 18 }} />
                      </ListItemDecorator>
                      <Typography level='body-sm'>{t('common.logout')}</Typography>
                    </MenuItem>
                  </Menu>
                </Dropdown>
              </Box>
            </>
          ) : (
            <>
              <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1, alignItems: 'center' }}>
                <Button component={Link} to='/login' variant='plain' color='neutral' size='sm' sx={{ color: 'text.primary' }}>
                  {t('auth.signIn')}
                </Button>
                <Button component={Link} to='/register' size='sm'>
                  {t('header.getStarted')}
                </Button>
                <HeaderUtils />
              </Box>
            </>
          )}
        </Stack>
      </Sheet>

      {/* Bug Report Modal */}
      <BugReportModal open={bugReportOpen} onClose={() => setBugReportOpen(false)} onSubmit={handleBugSubmit} />

      {/* Success Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        color={snackbar.color}
        variant='soft'
      >
        {snackbar.message}
      </Snackbar>

      {/* Mobile Navigation Drawer */}
      <Drawer
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        anchor='right'
        size='sm'
        sx={{
          '& .MuiDrawer-content': {
            bgcolor: 'background.surface'
          }
        }}
      >
        {/* Drawer Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Typography level='title-lg' fontWeight={600}>
            {t('header.drawer.menu')}
          </Typography>
          <IconButton variant='plain' size='sm' onClick={() => setMobileMenuOpen(false)} aria-label={t('common.close')}>
            <CloseRounded />
          </IconButton>
        </Box>

        {/* Drawer Content */}
        <Box sx={{ p: 2 }}>
          <List>
            {/* Browse - logged nav only (non-logged branch renders its own Browse) */}
            {user && (
              <ListItem>
                <ListItemButton
                  component={Link}
                  to='/browse'
                  onClick={() => setMobileMenuOpen(false)}
                  selected={isActive('/browse')}
                  sx={{
                    bgcolor: isActive('/browse') ? 'primary.softBg' : 'transparent'
                  }}
                >
                  <ListItemDecorator>
                    <SchoolRounded />
                  </ListItemDecorator>
                  {t('public.browse')}
                </ListItemButton>
              </ListItem>
            )}

            {user && <ListDivider sx={{ my: 1 }} />}

            {user ? (
              <>
                {[
                  { name: t('header.study'), path: '/study', icon: <AutoStoriesRounded /> },
                  { name: t('header.books'), path: '/books', icon: <MenuBookRounded /> },
                  { name: t('header.boards'), path: '/boards', icon: <SpaceDashboardRounded /> },
                  { name: t('annualPlanning.title'), path: '/annual-planning', icon: <TimelineRounded /> }
                ].map((item) => (
                  <ListItem key={item.name}>
                    <ListItemButton component={Link} to={item.path} onClick={() => setMobileMenuOpen(false)} selected={isActive(item.path)}>
                      <ListItemDecorator>{item.icon}</ListItemDecorator>
                      {item.name}
                    </ListItemButton>
                  </ListItem>
                ))}

                <ListDivider sx={{ my: 1 }} />
                <ListItem>
                  <ListItemButton component={Link} to='/profile' onClick={() => setMobileMenuOpen(false)}>
                    <ListItemDecorator>
                      <PersonRounded />
                    </ListItemDecorator>
                    {t('common.profile')}
                  </ListItemButton>
                </ListItem>
                <ListItem>
                  <ListItemButton component={Link} to='/settings' onClick={() => setMobileMenuOpen(false)}>
                    <ListItemDecorator>
                      <SettingsRounded />
                    </ListItemDecorator>
                    {t('common.settings')}
                  </ListItemButton>
                </ListItem>
                <ListItem>
                  <ListItemButton component={Link} to='/settings/agent' onClick={() => setMobileMenuOpen(false)}>
                    <ListItemDecorator>
                      <AutoAwesomeRounded />
                    </ListItemDecorator>
                    {petName || t('agent.defaultName')}
                  </ListItemButton>
                </ListItem>

                {settings.enabled && (
                  <>
                    <ListDivider sx={{ my: 1 }} />
                    <ListItem>
                      <ListItemButton
                        onClick={() => {
                          setShowWidget(!showWidget)
                          setMobileMenuOpen(false)
                        }}
                        selected={isTimerActive}
                      >
                        <ListItemDecorator>
                          <TimerRounded />
                        </ListItemDecorator>
                        {showTimerReadout ? `${t('common.pomodoro')} (${timerReadout})` : t('common.pomodoro')}
                      </ListItemButton>
                    </ListItem>
                  </>
                )}

                <ListDivider sx={{ my: 1 }} />
                <ListItem>
                  <ListItemButton
                    onClick={() => {
                      setMobileMenuOpen(false)
                      logout()
                    }}
                    color='danger'
                  >
                    <ListItemDecorator>
                      <LogoutRounded />
                    </ListItemDecorator>
                    {t('common.logout')}
                  </ListItemButton>
                </ListItem>
              </>
            ) : (
              <>
                {/* Get Started */}
                <ListItem>
                  <ListItemButton
                    component={Link}
                    to='/register'
                    onClick={() => setMobileMenuOpen(false)}
                    color='primary'
                    sx={{ borderRadius: 'sm', fontWeight: 700 }}
                  >
                    <ListItemDecorator>
                      <PersonAddRounded />
                    </ListItemDecorator>
                    {t('header.getStarted')}
                  </ListItemButton>
                </ListItem>

                {/* Sign In */}
                <ListItem>
                  <ListItemButton component={Link} to='/login' onClick={() => setMobileMenuOpen(false)} sx={{ borderRadius: 'sm' }}>
                    <ListItemDecorator>
                      <LoginRounded />
                    </ListItemDecorator>
                    {t('auth.signIn')}
                  </ListItemButton>
                </ListItem>

                <ListDivider sx={{ my: 1 }} />

                {/* Nav links */}
                {guestNav.map((item) => (
                  <ListItem key={item.path}>
                    <ListItemButton
                      component={Link}
                      to={item.to}
                      onClick={() => setMobileMenuOpen(false)}
                      selected={isActive(item.path)}
                      sx={{ borderRadius: 'sm' }}
                    >
                      <ListItemDecorator>{item.icon}</ListItemDecorator>
                      {item.name}
                    </ListItemButton>
                  </ListItem>
                ))}

                <ListDivider sx={{ my: 1 }} />

                {/* Utilities */}
                <ListItem>
                  <HeaderUtils variant='drawer' />
                </ListItem>
              </>
            )}
          </List>
        </Box>
      </Drawer>
    </>
  )
}

export default Header
